// Storybook.tsx — the living book.
//
// Empty book  → the prologue (a few written-for-everyone chapters that fade
//               away the moment the reader has pages of their own).
// Has pages   → the reader's own book: each saved reflection, bound as a page,
//               oldest to newest, turned one leaf at a time.

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getChapters } from "../utils/content";
import {
  getBookPages,
  deleteBookPage,
  exportBookPage,
  formatFolio,
  formatBegun,
  BookPage,
} from "../utils/book";
import BookLeaf from "../components/BookLeaf";
import { Wind, Leaf, Feather } from "lucide-react";
import { useSwipe } from "../hooks/useSwipe";
import ChapterNavMenu from "../components/ChapterNavMenu";
import LottieAnimation from "../components/LottieAnimation";
import fallingLeaves from "../assets/animations/falling-leaves.json";
import wind from "../assets/animations/wind.json";
import { trackAction } from "../utils/umami";

const Button = ({ children, onClick, className = "", variant = "default", disabled = false }: any) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md transition-all font-medium text-sm disabled:opacity-30 disabled:cursor-default ${
      variant === "ghost"
        ? "bg-transparent text-[#6e6861] hover:text-[#3c3a37]"
        : "bg-[#3c3a37] text-[#f8f4ef] hover:bg-[#2e2c2a]"
    } ${className}`}
  >
    {children}
  </button>
);

// Gentle page-turn — a fresh leaf settling in from the direction of travel.
const turnVariants = {
  enter: (dir: number) => ({ opacity: 0, rotateY: dir >= 0 ? 10 : -10, x: dir >= 0 ? 40 : -40 }),
  center: { opacity: 1, rotateY: 0, x: 0 },
};

const paragraphs = (text: string) => text.split("\n").filter((l) => l.trim() !== "");

const dropCap =
  "first-letter:font-serif first-letter:text-5xl sm:first-letter:text-6xl first-letter:font-semibold first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.72] first-letter:text-[#3c3a37]";
const bodyPara = "mb-4 text-[1.02rem] leading-[1.9] text-[#4a463f] font-serif";

export default function Storybook() {
  const navigate = useNavigate();
  const [pages] = useState<BookPage[]>(() => getBookPages());
  const hasBook = pages.length > 0;

  const [showAnimation, setShowAnimation] = useState(true);
  const scrollTop = useRef<HTMLDivElement>(null);

  return (
    <>
      {showAnimation ? (
        <LottieAnimation animation={fallingLeaves} opacity={0.28} scale={2.35} speed={0.8} delay={2000} zIndex={-1} />
      ) : (
        <LottieAnimation animation={wind} opacity={0.28} scale={3.5} speed={0.8} delay={1000} zIndex={-1} />
      )}

      <button
        onClick={() => {
          trackAction("Storybook - showAnimation clicked");
          setShowAnimation((prev) => !prev);
        }}
        className="fixed bottom-4 right-4 z-[200] inline-flex items-center gap-2 bg-white/70 backdrop-blur-md border border-[#e4dfd8] px-4 py-2 rounded-full text-sm shadow hover:bg-white transition"
      >
        {showAnimation ? (
          <><Wind size={15} color="#5d7895" strokeWidth={1.9} aria-hidden="true" /> Hold the breeze</>
        ) : (
          <><Leaf size={15} color="#5f8059" strokeWidth={1.9} aria-hidden="true" /> Let it flow</>
        )}
      </button>

      {/* The prologue's chapter menu only makes sense before the book begins */}
      {!hasBook && <ChapterNavMenu />}

      <div ref={scrollTop} className="w-full flex justify-center px-2">
        {hasBook ? (
          <PersonalBook pages={pages} navigate={navigate} scrollTop={scrollTop} />
        ) : (
          <Prologue navigate={navigate} scrollTop={scrollTop} />
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The reader's own book                                               */
/* ------------------------------------------------------------------ */

function PersonalBook({ pages: initialPages, navigate, scrollTop }: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState<BookPage[]>(initialPages);
  const total = pages.length;
  const begun = useMemo(() => formatBegun(pages), [pages]);
  const dir = useRef(0);

  const [idx, setIdx] = useState(() => {
    const fromUrl = parseInt(searchParams.get("page") || "", 10);
    if (!isNaN(fromUrl) && fromUrl >= 1 && fromUrl <= total) return fromUrl - 1;
    const saved = parseInt(localStorage.getItem("breatheBookPage") || "", 10);
    if (!isNaN(saved) && saved >= 0 && saved < total) return saved;
    return total - 1; // default to the newest page
  });

  useEffect(() => {
    localStorage.setItem("breatheBookPage", String(idx));
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("page", String(idx + 1));
        return p;
      },
      { replace: true }
    );
    scrollTop.current?.scrollIntoView({ behavior: "smooth" });
  }, [idx]);

  const go = (delta: number) => {
    dir.current = delta;
    const target = Math.min(total - 1, Math.max(0, idx + delta));
    if (target === idx) return; // already at the edge — no turn
    trackAction(
      `Book - Turn page ${delta > 0 ? "forward" : "back"} (page ${idx + 1} → ${target + 1} of ${total})`
    );
    setIdx(target);
  };

  const swipe = useSwipe(() => go(1), () => go(-1));

  const page = pages[idx];
  if (!page) return null;

  const handleExport = () => {
    trackAction("Book - Export page");
    exportBookPage(page);
  };

  const handleLetGo = () => {
    trackAction("Book - Let page go");
    if (!window.confirm("Let this page go? It won't come back.")) return;
    const remaining = deleteBookPage(idx);
    if (remaining.length === 0) {
      localStorage.removeItem("breatheBookPage");
      navigate("/reflect");
      return;
    }
    setPages(remaining);
    setIdx((i) => Math.min(i, remaining.length - 1));
  };

  const footer = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => go(-1)} disabled={idx === 0}>
          ← Back a page
        </Button>
        <Button variant="ghost" onClick={() => go(1)} disabled={idx === total - 1}>
          Turn the page →
        </Button>
      </div>

      <div className="text-center">
        <Button
          onClick={() => {
            trackAction("Book - Write today's page");
            navigate("/reflect");
          }}
          className="inline-flex items-center gap-2"
        >
          <Feather size={16} strokeWidth={1.9} aria-hidden="true" /> Write today’s page
        </Button>
      </div>

      <div className="flex items-center justify-center gap-3 text-[0.72rem] text-[#b4a98f]">
        <button onClick={handleExport} className="hover:text-[#8c8371] transition">
          Export this page
        </button>
        <span>·</span>
        <button onClick={handleLetGo} className="hover:text-[#8c8371] transition">
          Let this page go
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-[680px] flex flex-col items-center">
      <p className="mb-4 text-center text-sm text-[#8c8371] font-serif italic">
        Your book · {total} {total === 1 ? "page" : "pages"}
        {begun ? ` · ${begun}` : ""}
      </p>

      <div className="w-full flex justify-center" style={{ perspective: 1500 }} {...swipe}>
          <motion.div
            key={page.id}
            custom={dir.current}
            variants={turnVariants}
            initial="enter"
            animate="center"
            transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
            style={{ transformOrigin: "left center" }}
            className="w-full flex justify-center"
          >
            <BookLeaf folio={formatFolio(page.date)} pageLabel={`Page ${idx + 1} of ${total}`} footer={footer}>
              <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#3c3a37] text-center mb-6 leading-snug">
                {page.title}
              </h1>
              {paragraphs(page.body).map((line, i) => (
                <p key={i} className={i === 0 ? `${bodyPara} ${dropCap}` : bodyPara}>
                  {line}
                </p>
              ))}
            </BookLeaf>
          </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The prologue — shown only until the book has pages of its own       */
/* ------------------------------------------------------------------ */

function Prologue({ navigate, scrollTop }: any) {
  const chapters = getChapters(navigate);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const chapterSlug = searchParams.get("chapter");
  const dir = useRef(0);

  const [current, setCurrent] = useState(() => {
    const saved = localStorage.getItem("breatheBookmark");
    const index = saved ? parseInt(saved, 10) : 0;
    return !isNaN(index) && index < chapters.length ? index : 0;
  });

  const chapter = chapters[current];

  useEffect(() => {
    if (chapterSlug) {
      const index = chapters.findIndex((c) => c.slug === chapterSlug);
      if (index !== -1) {
        setCurrent(index);
        localStorage.setItem("breatheBookmark", index.toString());
      }
    } else if (chapter) {
      setSearchParams({ ...currentParams, chapter: chapter.slug });
    }
  }, [chapterSlug]);

  useEffect(() => {
    scrollTop.current?.scrollIntoView({ behavior: "smooth" });
  }, [current]);

  const goTo = (index: number, direction: number) => {
    dir.current = direction;
    setSearchParams({ ...currentParams, chapter: chapters[index].slug });
  };

  const next = () => {
    trackAction(`Storybook - Turn Page clicked from ${chapter.title}`);
    if (current < chapters.length - 1) goTo(current + 1, 1);
  };
  const back = () => {
    trackAction(`Storybook - Go Back from ${chapter.title}`);
    if (current > 0) goTo(current - 1, -1);
  };
  const reset = () => {
    trackAction(`Storybook - Take to beginning clicked from ${chapter.title}`);
    localStorage.removeItem("breatheBookmark");
    goTo(0, -1);
  };

  const swipe = useSwipe(next, back);

  const footer = (
    <div className="flex flex-col gap-4">
      <div className="text-center">
        <Button onClick={chapter.onClick}>{chapter.cta}</Button>
      </div>
      <p className="text-[0.85rem] text-center italic text-[#a29a89]">{chapter.undernote}</p>
      <div className="flex justify-between items-center">
        {current > 0 ? (
          <Button variant="ghost" onClick={back}>
            ← Go back
          </Button>
        ) : (
          <span />
        )}
        {current < chapters.length - 1 && (
          <Button variant="ghost" onClick={next}>
            Turn the page →
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-[680px] flex flex-col items-center">
      <div className="flex justify-between items-center mb-4 w-full px-1">
        <span className="text-sm text-[#8c8371] font-serif italic">
          Chapter {current + 1} of {chapters.length}
        </span>
        {current > 0 && (
          <button onClick={reset} className="text-xs text-[#a29a89] hover:text-[#6e6861] transition">
            Take me to the beginning
          </button>
        )}
      </div>

      <div className="w-full flex justify-center" style={{ perspective: 1500 }} {...swipe}>
          <motion.div
            key={chapter.id}
            custom={dir.current}
            variants={turnVariants}
            initial="enter"
            animate="center"
            transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
            style={{ transformOrigin: "left center" }}
            className="w-full flex justify-center"
          >
            <BookLeaf folio={chapter.number} pageLabel={`Chapter ${current + 1} of ${chapters.length}`} footer={footer}>
              <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#3c3a37] text-center mb-6 leading-snug">
                {chapter.title}
              </h1>
              {paragraphs(chapter.content).map((line, i) => (
                <p key={i} className={i === 0 ? `${bodyPara} ${dropCap}` : bodyPara}>
                  {line}
                </p>
              ))}
            </BookLeaf>
          </motion.div>
      </div>
    </div>
  );
}
